import { cn } from "@/libs/utils";
import { useEffect, useMemo, useRef, useState } from "react";

export function useStaggerAnimation({
  threshold = 0,
  rootMargin = "0px 0px -25% 0px",
  once = true,
  disabled = false,
}: {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
  disabled?: boolean;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<any>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (disabled) {
          setIsVisible(true);
          return;
        }
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          if (once) {
            observer.unobserve(element);
          }
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold, rootMargin, once, disabled]);

  const className = useMemo(() => {
    return disabled
      ? ""
      : cn(
          "transition-[opacity,transform] ease-in-out duration-1000",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6",
        );
  }, [isVisible, disabled]);

  return {
    ref,
    isVisible,
    className,
    delayFunc: (index: number) => `${disabled ? 0 : index * 120}ms`,
  };
}
